<template>
	<div
		v-if="ready"
		class="container-fluid"
	>

		<div class="row center">
			<div class="col-lg-12">
				<h1 class="main-content-title">
					{{ translations.navigation.schedules }} ({{ pagination.total }})
					<el-button
						v-if="isTeamOwner || isTeamAdmin"
						@click="userTeam.plan.expired
							? openTrialExpiredDialog()
							: isPlanExceed(schedules.length, 'maxNoSchedules')
								? openPlanLimitDialog()
								: openAddDialog()
						"
						type="primary"
						plain
					>
						{{ translations.labels.addNew }}
					</el-button>
				</h1>
			</div>
		</div>

		<div class="row center">
			<div class="col-lg-12">
				<div class="box">

					<Info
						type="schedules"
						:text="translations.labels.schedulesText"
					/>

					<!-- mobile cards -->
					<div v-if="isMobile()" class="box-body">

						<template v-if="schedules.length">

							<div
								v-for="(card, index) in schedules"
								:key="index"
								class="card"
							>
								<div class="card-settings">
									<span></span>
									<el-dropdown
										v-if="!userTeam.plan.expired && !isTeamViewer"
										@command="handleCommand"
										size="medium"
										placement="bottom-end"
									>
										<el-button plain size="mini">
											<span class="el-icon-s-tools" />
										</el-button>
										<el-dropdown-menu slot="dropdown">
											<el-dropdown-item
												:command="{ action: 'setup', id: card.id }"
											>
												{{ translations.labels.setup }}
											</el-dropdown-item>
											<el-dropdown-item
												v-if="isTeamOwner || isTeamAdmin"
												:command="{ action: 'assign', id: card }"
												:disabled="!checkAssignDevice(card.settings.resolution)"
											>
												{{ card.used.inUse ? translations.labels.reassign : translations.labels.assign }}
											</el-dropdown-item>
											<el-dropdown-item
												:command="{ action: 'edit', id: card.id }"
											>
												{{ translations.labels.edit }}
											</el-dropdown-item>
											<el-dropdown-item
												v-if="isTeamOwner || isTeamAdmin"
												:command="{ action: 'delete', id: card.id }"
												:disabled="card.used.inUse"
											>
												{{ translations.labels.delete }}
											</el-dropdown-item>
										</el-dropdown-menu>
									</el-dropdown>
								</div>
								<div class="card-title">
									{{ card.title }}
								</div>
								<div class="card-data">
									<div class="card-data-row">
										<span class="color-light">{{ translations.labels.size }}:</span><br />
										{{ convertResolution(card.settings.resolution) }}
									</div>
									<div class="card-data-row">
										<span class="color-light">{{ translations.labels.createdAt }}:</span><br />
										{{ convertDate(card.createdAt, 'date') }}
									</div>
									<div class="card-data-row color-light">
										<div v-if="card.used.inUse">
											{{ createString(translations.labels.currentlyUsedOnDevices, [card.used.devices], false, false) }}
										</div>
										<div v-else>
											{{ translations.labels.notInUse }}
										</div>
									</div>
								</div>
							</div>

						</template>

						<el-empty
							v-else
							:description="translations.labels.nothingFound"
							:image-size="120"
						/>

					</div>
					<!-- /mobile cards -->

					<!-- table -->
					<div v-else class="box-body">

						<div class="table-header">
							<div class="row">
								<div class="col-lg-12">
									<div class="flex-between">
										<SearchElement
											:disabled="schedules.length === 0"
											:processing="processing.search"
										/>
										<div v-if="schedules.length" class="table-header-filters">
											<Sort />
										</div>
									</div>
								</div>
							</div>
							<SearchFilter :search="search" />
						</div>

						<template v-if="schedules.length">
							<el-table
								v-loading="loading"
								:data="schedules"
								:show-header="false"
								style="width: 100%"
							>
							<el-table-column width="40">
								<template slot-scope="scope">
									<div class="table-icons">

										<el-tooltip
											v-if="scope.row.used.inUse"
											:content="translations.labels.scheduleInUse"
											effect="dark"
											placement="top"
										>
											<i class="el-icon-circle-check color-active" />
										</el-tooltip>
										<i v-else class="el-icon-remove-outline" style="opacity: .3" />

									</div>
								</template>
							</el-table-column>
								<el-table-column prop="title">
									<template slot-scope="scope">
										<div class="table-meta-data">
											<a
												v-if="userTeam.plan.expired"
												@click.prevent="openTrialExpiredDialog()"
												href="#"
												class="table-meta-data-title"
											>
												{{ scope.row.title }}
											</a>
											<router-link
												v-else
												:to="`/schedules/${scope.row.id}`"
												class="table-meta-data-title"
											>
												{{ scope.row.title }}
											</router-link>
											<div class="table-meta-data-info">
												{{ translations.labels.size }}:
												<span class="color-text">
													{{ convertResolution(scope.row.settings.resolution) }}
												</span>
												<div v-if="scope.row.used.inUse">
													{{ createString(translations.labels.currentlyUsedOnDevices, [scope.row.used.devices], false, false) }}
												</div>
												<div v-else>
													{{ translations.labels.notInUse }}
												</div>
											</div>
										</div>
									</template>
								</el-table-column>
								<el-table-column prop="createdAt" width="120">
									<template slot-scope="scope">
										<div class="table-small align-center">
											<div class="color-light">
												{{ translations.labels.createdAt }}:
											</div>
											{{ convertDate(scope.row.createdAt, 'date') }}
										</div>
									</template>
								</el-table-column>
								<el-table-column align="right" width="100">
									<template slot-scope="scope">
										<div class="align-right">
											<el-dropdown
												v-if="!userTeam.plan.expired && !isTeamViewer"
												@command="handleCommand"
												size="medium"
												placement="bottom-end"
											>
												<el-button plain size="mini">
													<span class="el-icon-s-tools" />
												</el-button>
												<el-dropdown-menu slot="dropdown">
													<el-dropdown-item
														:command="{ action: 'setup', id: scope.row.id }"
													>
														{{ translations.labels.setup }}
													</el-dropdown-item>
													<el-dropdown-item
														v-if="isTeamOwner || isTeamAdmin"
														:command="{ action: 'assign', id: scope.row }"
														:disabled="!checkAssignDevice(scope.row.settings.resolution)"
													>
														{{ scope.row.used.inUse ? translations.labels.reassign : translations.labels.assign }}
													</el-dropdown-item>
													<el-dropdown-item
														:command="{ action: 'edit', id: scope.row.id }"
													>
														{{ translations.labels.edit }}
													</el-dropdown-item>
													<el-dropdown-item
														v-if="isTeamOwner || isTeamAdmin"
														:command="{ action: 'delete', id: scope.row.id }"
														:disabled="scope.row.used.inUse"
													>
														{{ translations.labels.delete }}
													</el-dropdown-item>
												</el-dropdown-menu>
											</el-dropdown>
										</div>
									</template>
								</el-table-column>
							</el-table>
							<Pagination :pagination="pagination" />
						</template>

						<el-empty
							v-else
							:description="translations.labels.nothingFound"
							:image-size="150"
						/>

					</div>
					<!-- /table -->

				</div>
			</div>
		</div>

		<!-- delete dialog -->
		<DeleteConfirmation
			:show="dialog.delete"
			:processing="processing.delete"
			:message="translations.messages.warningRemoveSchedule"
			:label="translations.labels.deleteSchedule"
			confirmAction="proceed-delete"
			cancelAction="cancel-delete"
		/>
		<!-- /delete dialog -->

		<!-- add schedule dialog -->
		<el-dialog
			@opened="openedAddDialog"
			@closed="closedAddDialog"
			:title="translations.labels.addNewSchedule"
			:visible.sync="dialog.add"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="450px"
		>
			{{ translations.messages.warningAddSchedule }}
			<hr />
			<el-form
				:model="form"
				:rules="rules"
				ref="add"
			>
				<div class="row">
					<div class="col-lg-12">
						<label class="required">
							{{ translations.labels.title }}
						</label>
					</div>
					<div class="col-lg-12">
						<el-form-item>
							<el-input
								v-model="form.title"
								:placeholder="translations.labels.enterTitle"
								ref="scheduleTitle"
								clearable
							/>
						</el-form-item>
					</div>
				</div>
				<div class="row">
					<div class="col-lg-12">
						<label class="required">
							{{ translations.labels.size }}
						</label>
					</div>
					<div class="col-lg-12">
						<el-form-item>
							<el-select
								v-model="tempResolution.resolution"
								:placeholder="translations.labels.chooseResolution"
								style="width: 100%"
							>
								<el-option
									v-for="size in $config.deviceSizes"
									:key="size"
									:value="size"
									:label="translations.labels[`${size}`]"
								>
									<div class="flex-start">
										<span>
											<img :src="`/images/devices/${size}.png`" class="el-select-dropdown__item-device" />
										</span>
										<span>
											{{ translations.labels[`${size}`] }}
										</span>
									</div>
								</el-option>
							</el-select>
						</el-form-item>
					</div>
				</div>
				<div
					v-if="tempResolution.resolution === 'custom'"
					class="row"
				>
					<div class="col-lg-12">
						<div class="row">
							<div class="col-lg-6 col-xs-6">
								<el-form-item>
									<el-input
									v-model="tempResolution.resolutionWidth"
									:placeholder="translations.labels.enterWidth"
									type="text"
									clearable
									/>
								</el-form-item>
							</div>
							<div class="col-lg-6 col-xs-6">
								<el-form-item>
									<el-input
									v-model="tempResolution.resolutionHeight"
									:placeholder="translations.labels.enterHeight"
									type="text"
									clearable
									/>
								</el-form-item>
							</div>
						</div>
					</div>
				</div>
				<div class="row">
					<div class="col-lg-12 align-right">
						<el-button
							@click="submitForm('add')"
							:loading="processing.add"
							:disabled="isDisabledAddSchedule"
							:type="isDisabledAddSchedule ? 'info' : 'primary'"
							plain
						>
							{{ translations.labels.addSchedule }}
						</el-button>
						<el-button
							@click="closeAddDialog()"
							plain
						>
							{{ translations.labels.cancel }}
						</el-button>
					</div>
				</div>
			</el-form>
		</el-dialog>
		<!-- /add schedule dialog -->

		<!-- edit schedule dialog -->
		<el-dialog
			@opened="openedEditDialog"
			@closed="closedEditDialog"
			:title="translations.labels.editScheduleData"
			:visible.sync="dialog.edit"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="450px"
		>
			{{ translations.messages.warningEditSchedule }}
			<hr />
			<el-form
				:model="form"
				:rules="rules"
				ref="edit"
			>
				<div class="row">
					<div class="col-lg-12">
						<label class="required">
							{{ translations.labels.title }}
						</label>
					</div>
					<div class="col-lg-12">
						<el-form-item>
							<el-input
								v-model="form.title"
								:placeholder="translations.labels.enterTitle"
								:disabled="isTeamViewer || isTeamEditor"
								ref="scheduleTitle"
								type="text"
								clearable
							/>
						</el-form-item>
					</div>
				</div>
				<div class="row">
					<div class="col-lg-12">
						<label class="required">
							{{ translations.labels.size }}
							<span
								v-if="form.calendarEntries.length > 0 || form.used.inUse"
								:data-tooltip="translations.labels.tooltipResolutionScheduleDisabled"
							>
								<i class="el-icon-info" />
							</span>
						</label>
					</div>
					<div class="col-lg-12">
						<el-form-item>
							<el-select
								v-model="tempResolution.resolution"
								:placeholder="translations.labels.chooseResolution"
								:disabled="(form.calendarEntries.length > 0 || form.used.inUse) || (isTeamViewer || isTeamEditor)"
								style="width: 100%"
							>
								<el-option
									v-for="size in $config.deviceSizes"
									:key="size"
									:value="size"
									:label="translations.labels[`${size}`]"
								>
									<div class="flex-start">
										<span>
											<img :src="`/images/devices/${size}.png`" class="el-select-dropdown__item-device" />
										</span>
										<span>
											{{ translations.labels[`${size}`] }}
										</span>
									</div>
								</el-option>
							</el-select>
						</el-form-item>
					</div>
				</div>
				<div
					v-if="tempResolution.resolution === 'custom'"
					class="row"
				>
					<div class="col-lg-6 col-xs-6">
						<el-form-item>
							<el-input
								v-model="tempResolution.resolutionWidth"
								:placeholder="translations.labels.enterWidth"
								:disabled="(form.calendarEntries.length > 0 || form.used.inUse) || (isTeamViewer || isTeamEditor)"
								type="text"
								clearable
							/>
						</el-form-item>
					</div>
					<div class="col-lg-6 col-xs-6">
						<el-form-item>
							<el-input
								v-model="tempResolution.resolutionHeight"
								:placeholder="translations.labels.enterHeight"
								:disabled="(form.calendarEntries.length > 0 || form.used.inUse) || (isTeamViewer || isTeamEditor)"
								type="text"
								clearable
							/>
						</el-form-item>
					</div>
				</div>
				<div class="row">
					<div class="col-lg-12 align-right">
						<el-button
							@click="submitForm('edit')"
							:loading="processing.edit"
							:disabled="isDisabledAddSchedule || isTeamViewer || isTeamEditor"
							:type="isDisabledAddSchedule || isTeamViewer || isTeamEditor ? 'info' : 'primary'"
							native-type="submit"
							plain
						>
							{{ translations.labels.saveChanges }}
						</el-button>
						<el-button
							@click="closeEditDialog()"
							plain
						>
							{{ translations.labels.cancel }}
						</el-button>
					</div>
				</div>
			</el-form>
		</el-dialog>
		<!-- /edit schedule dialog -->

		<!-- assign -->
		<AssignToDevice
			:current="this.tempAssign"
			:devices="devices"
			:processing="processing.assign"
			:show="dialog.assign"
			:title="translations.labels.assignSchedule"
			type="schedule"
			:warning="translations.messages.warningAssignSchedule"
			:warningApply="translations.messages.warningAppliedImmediately"
		/>
		<!-- /assign -->

	</div>
</template>

<script>
import {
	_getSchedules,
	_addSchedule,
	_updateSchedule,
	_deleteSchedule
} from '@/services/api/schedules'
import { _getAllDevices, _assignToDevice } from '@/services/api/devices'

import { EventBus } from '@/services/event-bus'

import arrayFromString from '@/services/mixins/arrayFromString'
import convertDate from '@/services/mixins/convertDate'
import setResolution from '@/services/mixins/setResolution'
import sortArray from '@/services/mixins/sortArray'
import convertResolution from '@/services/mixins/convertResolution'
import isMobile from '@/services/mixins/isMobile'
import createString from '@/services/mixins/createString'

export default {
	name: 'SchedulesView',
	metaInfo () {
		return {
			title: this.translations.navigation.schedules
		}
	},
	mixins: [
		arrayFromString,
		convertDate,
		setResolution,
		sortArray,
		convertResolution,
		isMobile,
		createString
	],
	components: {
		AssignToDevice: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Dialog/AssignToDevice'),
		DeleteConfirmation: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Dialog/Delete'),
		Info: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Misc/Info'),
		Pagination: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Search/Pagination'),
		SearchElement: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Search/Element'),
		SearchFilter: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Search/Filter'),
		Sort: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Misc/Sort')
	},
	data () {
		return {
			deleteId: null,
			devices: [],
			dialog: {
				delete: false,
				add: false,
				edit: false,
				assign: false
			},
			form: {
				title: null,
				description: null,
				settings: {
					resolution: null,
					orientation: null
				},
				used: {
					inUse: null,
					devices: null,
					schedules: null
				},
				calendarEntries: []
			},
			loading: false,
			pagination: {
				total: null,
				pageSize: this.$config.paginationSize.table,
				currentPage: 1,
				sortCriteria: 'asc'
			},
			processing: {
				delete: false,
				add: false,
				edit: false,
				search: false,
				assign: false
			},
			rules: {
				title: [
					{ required: true, message: this.$store.state.translations.messages.enterTitle, trigger: 'blur' }
				]
			},
			schedules: [],
			selected: [],
			search: {
				field: 'title',
				value: '',
				filter: false,
				sortBy: 'title'
			},
			tempAssign: {},
			tempDevices: [],
			tempResolution: {
				resolution: null,
				resolutionWidth: null,
				resolutionHeight: null,
				columns: null,
				rows: null
			}
		}
	},
	computed: {
		isDisabledAddSchedule () {
			let invalidResolution = false
			if (this.tempResolution.resolution === 'custom') {
				invalidResolution = !this.tempResolution.resolutionHeight || !this.tempResolution.resolutionWidth
			}
			return !this.form.title || !this.tempResolution.resolution || invalidResolution
		}
	},
	watch: {
		'tempResolution': {
            handler () {
				this.setResolution()
            },
            deep: true
        }
	},
	created () {
		this.objectLength(this.userTeam) ? this.ajaxLoadData() : this.redirectTo('/team/fallback')
	},
	mounted () {
		EventBus.$on('change-pagination', (data) => this.changePage(data))
		EventBus.$on('reset-search', () => this.resetSearch())
		EventBus.$on('submit-search', (data) => this.submitSearch(data))
		EventBus.$on('cancel-delete', () => this.closeDeleteDialog())
		EventBus.$on('proceed-delete', () => this.ajaxDeleteSchedule())
		EventBus.$on('change-sort', (data) => this.changeSort(data))
		EventBus.$on('close-assign', () => this.closeAssignDialog())
		EventBus.$on('closed-assign', () => this.closedAssignDialog())
		EventBus.$on('submit-assign', (data) => this.ajaxAssignSchedule(data))
	},
	destroyed () {
		EventBus.$off('change-pagination')
		EventBus.$off('reset-search')
		EventBus.$off('submit-search')
		EventBus.$off('cancel-delete')
		EventBus.$off('proceed-delete')
		EventBus.$off('change-sort')
		EventBus.$off('close-assign')
		EventBus.$off('closed-assign')
		EventBus.$off('submit-assign')
    },
	methods: {

		ajaxLoadData () {
			this.ajaxLoadSchedules()
			this.ajaxLoadDevices()
		},
		ajaxLoadSchedules () {
			const data = {
				searchField: this.search.field,
				searchValue: this.search.value,
				sortBy: this.search.sortBy,
				currentPage: this.pagination.currentPage,
				pageSize: this.pagination.pageSize,
				sortCriteria: this.pagination.sortCriteria
			}
			_getSchedules(data)
				.then(response => {
					this.schedules = response.responseList
					this.pagination = {
						total: response.total,
						pageSize: response.pageSize,
						currentPage: response.currentPage,
						sortCriteria: this.pagination.sortCriteria
					}
					setTimeout (() => {
						this.removeLoader()
						this.loading = false
						this.processing.search = false
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.status
					setTimeout (() => {
						this.displayMessage( this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.loading = false
					}, 200)
				})
		},
		ajaxLoadDevices () {
			_getAllDevices()
				.then(response => {
					this.devices = response.length ? this.sortArray(response, 'title', 'asc') : []
				})
		},
		ajaxAddSchedule () {
			this.processing.add = true
			_addSchedule(this.form)
				.then(response => {
					this.redirectTo(`/schedules/${response.id}`)
					setTimeout (() => {
						this.displayMessage( this.translations.messages.successAddSchedule, 'success')
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage( this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.add = false
					}, 200)
				})
		},
		ajaxEditSchedule () {
			this.processing.edit = true
			_updateSchedule(this.form)
				.then(() => {
					this.ajaxLoadSchedules()
					setTimeout (() => {
						this.closeEditDialog()
						this.displayMessage( this.translations.messages.successUpdateSchedule, 'success')
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage( this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.edit = false
					}, 200)
				})
		},
		ajaxDeleteSchedule () {
			this.processing.delete = true
			_deleteSchedule(this.deleteId)
				.then(() => {
					this.ajaxLoadSchedules()
					setTimeout (() => {
						this.closeDeleteDialog()
						this.displayMessage( this.translations.messages.successDeleteSchedule, 'success')
						this.processing.delete = false
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					this.deleteId = null
					setTimeout (() => {
						this.displayMessage( this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.delete = false
					}, 200)
				})
		},
		ajaxAssignSchedule (data) {
			this.processing.assign = true
			_assignToDevice(data)
				.then(() => {
					this.ajaxLoadSchedules()
					setTimeout (() => {
						this.closeAssignDialog()
						this.displayMessage( this.translations.messages.successAssignSchedule, 'success')
						this.processing.assign = false
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage( this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.assign = false
					}, 200)
				})
		},

		submitForm (form) {
			this.$refs[form].validate((valid) => {
				if (valid) {
					if (form === 'add') {
						this.ajaxAddSchedule()
					}
					if (form === 'edit') {
						this.ajaxEditSchedule()
					}
				}
			})
		},
		submitSearch (data) {
			this.search.value = data
			this.search.filter = true
			this.processing.search = true
			this.loading = true
			this.ajaxLoadSchedules()
		},
		resetSearch () {
			this.pagination.currentPage = 1
			this.processing.search = false
			this.search = {
				filter: false,
				field: 'title',
				value: '',
				sortBy: 'title'
			}
			this.loading = true
			this.ajaxLoadSchedules()
		},
		openAddDialog () {
			this.dialog.add = true
		},
		openedAddDialog () {
			this.$nextTick(() => {
				this.$refs.add.clearValidate()
				this.$refs.scheduleTitle.focus()
			})
		},
		closeAddDialog () {
			this.dialog.add = false
			this.processing.add = false
		},
		closedAddDialog () {
			this.form = {
				title: null,
				description: null,
				settings: {
					resolution: null,
					orientation: null
				},
				used: {
					inUse: null,
					devices: null,
					schedules: null
				},
				calendarEntries: []
			}
			this.tempResolution = {
				resolution: null,
				resolutionWidth: null,
				resolutionHeight: null
			}
			this.$refs.add.clearValidate()
		},
		openDeleteDialog (id) {
			this.dialog.delete = true
			this.deleteId = id
		},
		closeDeleteDialog () {
			this.dialog.delete = false,
			this.processing.delete = false
			this.deleteId = null
		},
		openEditDialog (id) {
			this.form = this.clone(this.schedules.find(item => item.id === id))
			this.setTempResolution()
			this.dialog.edit = true
		},
		openedEditDialog () {
			this.$nextTick(() => {
				this.$refs.scheduleTitle.focus()
			})
		},
		closeEditDialog () {
			this.processing.edit = false
			this.dialog.edit = false
		},
		closedEditDialog () {
			this.$refs.edit.clearValidate()
			this.form = {
				title: null,
				description: null,
				settings: {
					resolution: null,
					orientation: null
				},
				used: {
					inUse: null,
					devices: null,
					schedules: null
				},
				calendarEntries: []
			}
			this.tempResolution = {
				resolution: null,
				resolutionWidth: null,
				resolutionHeight: null
			}
		},
		openAssignDialog (row) {
			this.dialog.assign = true
			this.tempAssign = row
		},
		closeAssignDialog () {
			this.dialog.assign = false
			this.processing.assign = false
		},
		closedAssignDialog () {
			this.tempAssign = {}
		},
		checkAssignDevice (resolution) {
			return this.devices.some(item => item.settings.resolution === resolution)
		},
		changePage (page) {
			this.pagination.currentPage = page
			this.loading = true
			this.ajaxLoadSchedules()
		},
		changeSort (data) {
			this.pagination.currentPage = 1
			this.pagination.sortCriteria = data.sortCriteria
			this.search.sortBy = data.sortBy
			this.loading = true
			this.ajaxLoadSchedules()
		},
		handleCommand (el) {
			if (el.action === 'setup') {
				this.redirectTo(`/schedules/${el.id}`)
			}
			if (el.action === 'assign') {
				this.openAssignDialog(el.id)
			}
			if (el.action === 'edit') {
				this.openEditDialog(el.id)
			}
			if (el.action === 'delete') {
				this.openDeleteDialog(el.id)
			}
		}

	}
}
</script>
